import React, { useEffect, useState } from "react";
import { ShieldCheck, Award, Users, Car } from "lucide-react";
import api from "../lib/api";

export default function AboutPage() {
  const [owner, setOwner] = useState(null);

  useEffect(() => {
    document.title = "About Us — Faisal Car Bazar";
    api.get("/owner").then((r) => setOwner(r.data));
  }, []);

  const stats = owner
    ? [
        { icon: Award, n: `${owner.years_experience}+`, l: "Years Experience" },
        { icon: Car, n: `${owner.cars_delivered}+`, l: "Cars Delivered" },
        { icon: Users, n: `${owner.happy_customers}+`, l: "Happy Customers" },
        { icon: ShieldCheck, n: "150+", l: "Inspection Points" },
      ]
    : [];

  return (
    <div data-testid="about-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="mb-12">
        <div className="eyebrow mb-4">About</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F5F5DC] font-light">
          A passion for cars. <br />
          <span className="text-[#DC143C] italic">A commitment to people.</span>
        </h1>
        <div className="luxe-divider" />
      </div>

      {owner && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-24">
          <div className="lg:col-span-5">
            <div className="relative">
              <img
                src={owner.photo_url}
                alt={owner.name}
                className="rounded-2xl w-full aspect-[4/5] object-cover border border-white/5"
                data-testid="owner-photo"
              />
              <div className="absolute -bottom-6 -right-6 glass p-6 rounded-2xl max-w-[200px]">
                <div className="font-display text-3xl text-[#D4AF37]">{owner.years_experience}+</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#F5F5DC]/70 mt-1">
                  Years in the industry
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="eyebrow mb-4">Meet the Founder</div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#F5F5DC] font-light leading-tight" data-testid="owner-name">
              {owner.name}
            </h2>
            <div className="text-[#D4AF37] text-sm tracking-wide mt-1" data-testid="owner-title">{owner.title}</div>
            <p className="text-[#A0A0A0] leading-relaxed mt-6 text-base" data-testid="owner-bio">
              {owner.bio}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-24">
        {stats.map((s) => (
          <div key={s.l} className="premium-card p-6 text-center">
            <s.icon className="mx-auto text-[#DC143C] mb-3" size={24} />
            <div className="font-display text-3xl text-[#F5F5DC]">{s.n}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0A0] mt-1">{s.l}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-24">
        <div className="premium-card p-8">
          <div className="eyebrow mb-4">Our Mission</div>
          <h3 className="font-display text-2xl text-[#F5F5DC] mb-4">Pre-Owned, Re-Imagined.</h3>
          <p className="text-sm text-[#A0A0A0] leading-relaxed">
            To transform how India buys and sells pre-owned cars by bringing transparency,
            premium quality, and unmatched after-sales service to every transaction.
          </p>
        </div>
        <div className="premium-card p-8">
          <div className="eyebrow mb-4">Our Vision</div>
          <h3 className="font-display text-2xl text-[#F5F5DC] mb-4">Trust. Delivered.</h3>
          <p className="text-sm text-[#A0A0A0] leading-relaxed">
            To be central India's most trusted name in premium pre-owned automobiles —
            recognized for honesty, quality assurance, and a customer-first ethos.
          </p>
        </div>
      </div>

      <div className="premium-card p-10 text-center">
        <div className="eyebrow justify-center inline-flex mb-4">Why Customers Trust Us</div>
        <h3 className="font-display text-3xl sm:text-4xl text-[#F5F5DC] font-light mb-6 max-w-3xl mx-auto">
          Every car is a promise. We earn that promise through 150+ inspection points,
          honest pricing and a handshake you can rely on.
        </h3>
      </div>
    </div>
  );
}
